// src/components/Pages/DispatchedBriefsDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Title,
    Text,
    useMantineTheme,
    useMantineColorScheme,
    Paper,
    Loader,
    Stack,
    Alert,
    Group,
    Accordion,
    Modal,
    Button,
    Pagination,
    Badge,
    Divider,
    Space,
} from '@mantine/core';
// <<< FIX: Added IconWorld for the region badge
import { IconSend, IconAlertCircle, IconWorld } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import BriefDisplay from '../Briefs/BriefDisplay';
import { getFirestore, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const brandColorMap = {
    'LoveMoney': 'rgb(174, 26, 18)',
    'LoveProperty': 'rgb(82, 110, 167)',
    'LoveFood': 'rgb(223, 82, 135)',
    'LoveExploring': 'rgb(5, 146, 159)',
};

function DispatchedBriefsDashboard() {
    const contentRef = useRef(null);
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { currentUser } = useAuth();

    // <<< FIX: Re-introduced the background image logic
    const { isBackgroundImageVisible } = useBackgroundImage();
    const headerTextColor = isBackgroundImageVisible ? theme.white : undefined;

    const [briefs, setBriefs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activePage, setPage] = useState(1);
    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
    const [selectedBriefForModal, setSelectedBriefForModal] = useState(null);

    const getBrandColor = (brand) => brandColorMap[brand] || 'rgb(122, 59, 91)';

    useEffect(() => {
        // Fetching logic remains the same
        const fetchDispatchedBriefs = async () => {
            if (!currentUser) {
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const db = getFirestore();
                const briefsCollection = collection(db, 'generated_briefs');
                const q = query(
                    briefsCollection,
                    where('status', '==', 'Dispatched'),
                    orderBy('creationTimestamp', 'desc')
                );

                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setBriefs(data);

            } catch (err) {
                console.error("Error fetching dispatched briefs:", err);
                setError("Failed to fetch dispatched briefs. This may be a permissions or index issue.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchDispatchedBriefs();
    }, [currentUser]);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [activePage]);

    const renderContent = () => {
        if (isLoading) { return (<Group justify="center" mt="xl"><Loader /><Text>Loading dispatched briefs...</Text></Group>); }
        if (error) { return (<Alert title="Error" color="red" icon={<IconAlertCircle />}>{error}</Alert>); }
        if (briefs.length === 0) { return <Text>No briefs are currently dispatched.</Text>; }

        const BRIEFS_PER_PAGE = 8;
        const totalPages = Math.ceil(briefs.length / BRIEFS_PER_PAGE);
        const startIndex = (activePage - 1) * BRIEFS_PER_PAGE;
        const endIndex = startIndex + BRIEFS_PER_PAGE;
        const briefsToShow = briefs.slice(startIndex, endIndex);

        return (
            <>
                <Accordion variant="separated" radius="md">
                    {briefsToShow.map(brief => (
                        <Accordion.Item value={brief.id} key={brief.id}>
                            <Accordion.Control>
                                <Stack gap="xs">
                                    <Group gap="xs" wrap="nowrap">
                                        <Text fw={500} >
                                            {brief.generatedBrief?.titleSuggestions?.writerHubAlternatives[0] || 'Untitled Brief'}
                                        </Text>
                                         {/* <Text c="dimmed">|</Text>*/}
                                        <Text size="sm" c="dimmed" fs="italic">
                                            Due: {formatDeadline(brief.deadline)},  {brief.assignedWriter || 'Not assigned'}
                                        </Text>
                                    </Group>
                                    <Text size="xs" c="dimmed">
                                        Created: {brief.creationTimestamp ? brief.creationTimestamp.toDate().toLocaleString() : 'Date not available'}
                                        {` | From: "${brief.requestInputs?.raw_title_idea}"`}
                                        {` | By: ${brief.userEmail || 'N/A'}`}
                                    </Text>
                                    <Group gap="xs" mt="xs">
                                        {brief.requestInputs?.target_brand && (
                                            <Badge color={getBrandColor(brief.requestInputs.target_brand)} variant="filled">{brief.requestInputs.target_brand}</Badge>
                                        )}
                                        {/* <<< FIX: Re-introduced the target_region badge */}
                                        {brief.requestInputs?.target_region && (
                                            <Badge variant="light" color="gray.5" leftSection={<IconWorld size={14} />}>
                                                {brief.requestInputs.target_region}
                                            </Badge>
                                        )}
                                        <Badge variant="filled" color="black">{brief.status}</Badge>
                                    </Group>
                                </Stack>
                            </Accordion.Control>
                            <Accordion.Panel>
                                <Stack>
                                    <Group justify="left">
                                        <Button
                                            variant="light"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedBriefForModal(brief.generatedBrief);
                                                openModal();
                                            }}
                                        >
                                            View Full Brief
                                        </Button>
                                    </Group>
                                    <Divider my="xs" label="Assignment Details" labelPosition="left" />
                                    <Text size="sm"><b>Assigned Writer:</b> {brief.assignedWriter || 'Not assigned'}</Text>
                                    {/* <<< FIX: More robust date handling */}
                                    <Text size="sm">
                                        <b>Deadline:</b> {formatDeadline(brief.deadline)}
                                    </Text>
                                    <Text size="sm"><b>Notes:</b></Text>
                                    <Paper withBorder p="xs" radius="sm" bg={isDark ? theme.colors.dark[6] : theme.colors.gray[0]}>
                                        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{brief.notes || 'No notes provided.'}</Text>
                                    </Paper>
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                    ))}
                </Accordion>
                <Group justify="center" mt="xl">
                    <Pagination total={totalPages} value={activePage} onChange={setPage} />
                </Group>
            </>
        );
    };


    const formatDeadline = (deadline) => {
        if (!deadline) {
            return 'No deadline set';
        }
        try {
            // Create a date object using our robust method
            const d = new Date(deadline.toDate ? deadline.toDate() : deadline);

            // Check if the date is invalid
            if (isNaN(d.getTime())) {
                return 'Invalid Date';
            }

            // Check if the time is midnight
            if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0) {
                // If it's midnight, return only the date string
                return d.toLocaleDateString('en-GB'); // Formats as DD/MM/YYYY
            } else {
                // Otherwise, return date and a clean time
                return d.toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                }); // Formats as DD/MM/YYYY, HH:MM
            }
        } catch (e) {
            return 'Error formatting date';
        }
    };


    return (
        // <<< FIX: The main <Box> container was missing, this restores the background functionality
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                overflow: 'auto',
            }}
        >
            <Container size="lg" py="xl" style={{ width: '100%', flexGrow: 1 }}>
                <Title order={1} ta="center" mb="xs" style={{ color: headerTextColor }}>
                    <IconSend color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} style={{ marginRight: theme.spacing.sm, verticalAlign: 'middle' }} />
                    Dispatched Briefs
                </Title>
                <Text size="xl" ta="center" mb="xl" style={{ color: headerTextColor }}>
                    A management-only view of all briefs currently dispatched to writers.
                </Text>
                <Paper ref={contentRef} withBorder p="xl" radius="md" shadow="md">
                    {renderContent()}
                </Paper>
                <Modal opened={modalOpened} onClose={closeModal} title="Brief Details" size="xl" scrollAreaComponent={Modal.NativeScrollArea}>
                    {selectedBriefForModal && <BriefDisplay brief={selectedBriefForModal} />}
                </Modal>
            </Container>
        </Box>
    );
}

export default DispatchedBriefsDashboard;